import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core'

import { LocalDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import {
  EraantymisenTila,
  Korttisuodatus,
  TilinpaatoskortinRivi,
  TilinpaatoskortinSarake,
  TilinpaatoksenVaihe,
  TyolistanYhteenvetokortit,
  YhteenvetokortinRivi,
  YhteenvetokortinTyyppi,
} from '../../../_jaettu-lemonator/model/tyolistan-yhteenvetokortit'

type RivikortinTyyppi = Exclude<YhteenvetokortinTyyppi, 'tilinpaatokset'>
type VaihenuolenSijainti = 'ensimmainen' | 'keskimmainen' | 'viimeinen'

interface KortinKohde {
  suodatus: Korttisuodatus
  maara: number
  tila: EraantymisenTila
  vihje: string
  aktiivinen: boolean
}

interface NaytettavaRivi extends KortinKohde {
  nimi: string
  erapaivaTeksti: string | null
  kirjauksia: number | null
  asiakasmaaraTeksti: string
}

interface NaytettavaKortti {
  tyyppi: RivikortinTyyppi
  otsikko: string
  yhteensa: number
  rivit: NaytettavaRivi[]
}

interface NaytettavaVaihenuoli extends KortinKohde {
  sijainti: VaihenuolenSijainti
}

interface NaytettavaTilinpaatosrivi {
  avain: string
  nimi: string
  erapaivaTeksti: string
  nuolet: NaytettavaVaihenuoli[]
  osingot: KortinKohde
}

const RIVIKORTIT: { tyyppi: RivikortinTyyppi; otsikko: string; kuvaus: string }[] = [
  { tyyppi: 'kirjaukset', otsikko: 'Kirjausta käsiteltävänä', kuvaus: 'Käsittelemättömät kirjaukset' },
  { tyyppi: 'kirjanpidot', otsikko: 'Kirjanpitoa tehtävänä', kuvaus: 'Tekemättömät kirjanpidot' },
  { tyyppi: 'alv', otsikko: 'ALV-ilmoitusta lähettämättä', kuvaus: 'Lähettämättömät alv-ilmoitukset' },
  { tyyppi: 'veroilmoitukset', otsikko: 'Veroilmoitusta lähettämättä', kuvaus: 'Lähettämättömät veroilmoitukset' },
]

const TILINPAATOKSEN_VAIHEET: { vaihe: TilinpaatoksenVaihe; nimi: string; sijainti: VaihenuolenSijainti }[] = [
  { vaihe: 'tekematta', nimi: 'Tekemättä', sijainti: 'ensimmainen' },
  { vaihe: 'allekirjoittamatta', nimi: 'Allekirjoituksessa', sijainti: 'keskimmainen' },
  { vaihe: 'rekisteroimatta', nimi: 'Odottaa rekisteröintiä', sijainti: 'viimeinen' },
]

const MYOHASSA_VIHJE = 'Myöhässä — eräpäivä ohitettu'

@Component({
  selector: 'app-tyolistan-yhteenvetokortit',
  templateUrl: './tyolistan-yhteenvetokortit.component.html',
  styleUrls: ['./tyolistan-yhteenvetokortit.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TyolistanYhteenvetokortitComponent {
  readonly kortit = input.required<TyolistanYhteenvetokortit | null>()
  readonly aktiivinenSuodatus = input.required<Korttisuodatus | null>()
  readonly tanaan = input.required<LocalDate>()

  readonly valitse = output<Korttisuodatus>()

  readonly tilinpaatoksenVaiheet = TILINPAATOKSEN_VAIHEET
  readonly luurankokortit = [0, 1, 2, 3, 4]
  readonly luurankorivit = [0, 1, 2]

  readonly naytettavatKortit = computed<NaytettavaKortti[]>(() => {
    const kortit = this.kortit()
    if (!kortit) {
      return []
    }
    return RIVIKORTIT.map(kortti => ({
      tyyppi: kortti.tyyppi,
      otsikko: kortti.otsikko,
      yhteensa: kortit[kortti.tyyppi].yhteensa,
      rivit: kortit[kortti.tyyppi].rivit.map(rivi => this._annaNaytettavaRivi(kortti.tyyppi, kortti.kuvaus, rivi)),
    }))
  })
  readonly tilinpaatostenYhteensa = computed(() => this.kortit()?.tilinpaatokset.yhteensa ?? 0)
  readonly tilinpaatosrivit = computed<NaytettavaTilinpaatosrivi[]>(() => this.kortit()?.tilinpaatokset.rivit.map(rivi => this._annaNaytettavaTilinpaatosrivi(rivi)) ?? [])

  constructor(private _dateService: DateService) {}

  valitseKohde(kohde: KortinKohde) {
    if (kohde.maara > 0) {
      this.valitse.emit(kohde.suodatus)
    }
  }

  private _annaNaytettavaRivi(tyyppi: RivikortinTyyppi, kuvaus: string, rivi: YhteenvetokortinRivi): NaytettavaRivi {
    const tila = rivi.eraantymisenTila ?? 'normaali'
    const maara = rivi.asiakasAvaimet.length
    return {
      ...this._annaKohde({ kortti: tyyppi, rivinAvain: rivi.avain, kuvaus, rivinNimi: rivi.nimi, jaksonKuukausi: rivi.kuukausi }, maara, tila, this._annaRivinVihje(tila, rivi.erapaiva)),
      nimi: rivi.nimi,
      erapaivaTeksti: rivi.erapaiva ? this._annaErapaivanTeksti(rivi.erapaiva, tila) : null,
      kirjauksia: rivi.kirjauksia ?? null,
      asiakasmaaraTeksti: maara === 1 ? '1 asiakas' : `${maara} asiakasta`,
    }
  }

  private _annaNaytettavaTilinpaatosrivi(rivi: TilinpaatoskortinRivi): NaytettavaTilinpaatosrivi {
    const rivinVihje = this._annaRivinVihje(rivi.eraantymisenTila, rivi.erapaiva)
    const nuolet = TILINPAATOKSEN_VAIHEET.map(vaihe => {
      const maara = rivi.sarakkeet[vaihe.vaihe].length
      const tila = maara > 0 ? rivi.eraantymisenTila : 'normaali'
      return {
        ...this._annaTilinpaatoksenKohde(rivi, vaihe.vaihe, `Tilinpäätökset: ${vaihe.nimi}`, maara, tila, maara > 0 ? rivinVihje : ''),
        sijainti: vaihe.sijainti,
      }
    })
    const osinkojenMaara = rivi.sarakkeet.osingot.length
    const osinkojenTila = osinkojenMaara > 0 ? rivi.osinkojenEraantymisenTila : 'normaali'
    return {
      avain: rivi.avain,
      nimi: rivi.nimi,
      erapaivaTeksti: this._annaErapaivanTeksti(rivi.erapaiva, rivi.eraantymisenTila),
      nuolet,
      osingot: this._annaTilinpaatoksenKohde(rivi, 'osingot', 'Tilinpäätökset: Osingot ilmoittamatta', osinkojenMaara, osinkojenTila, this._annaOsinkojenVihje(osinkojenTila)),
    }
  }

  private _annaTilinpaatoksenKohde(rivi: TilinpaatoskortinRivi, sarake: TilinpaatoskortinSarake, kuvaus: string, maara: number, tila: EraantymisenTila, vihje: string) {
    return this._annaKohde({ kortti: 'tilinpaatokset', rivinAvain: rivi.avain, sarake, kuvaus, rivinNimi: rivi.nimi, jaksonKuukausi: rivi.kuukausi }, maara, tila, vihje)
  }

  private _annaKohde(suodatus: Korttisuodatus, maara: number, tila: EraantymisenTila, vihje: string): KortinKohde {
    const aktiivinen = this.aktiivinenSuodatus()
    return {
      suodatus,
      maara,
      tila,
      vihje,
      aktiivinen: !!aktiivinen && aktiivinen.kortti === suodatus.kortti && aktiivinen.rivinAvain === suodatus.rivinAvain && aktiivinen.sarake === suodatus.sarake,
    }
  }

  private _annaRivinVihje(tila: EraantymisenTila, erapaiva: LocalDate | undefined) {
    if (tila === 'myohassa') {
      return MYOHASSA_VIHJE
    }
    if (tila === 'eraantyy' && erapaiva) {
      return this._onkoTanaan(erapaiva) ? 'Erääntyy tänään' : 'Erääntyy huomenna'
    }
    return ''
  }

  private _annaOsinkojenVihje(tila: EraantymisenTila) {
    if (tila === 'myohassa') {
      return 'Osinkoilmoitus myöhässä'
    }
    if (tila === 'eraantyy') {
      return 'Osinkoilmoitus erääntyy tänään tai huomenna'
    }
    return ''
  }

  private _annaErapaivanTeksti(erapaiva: LocalDate, tila: EraantymisenTila) {
    if (tila === 'eraantyy') {
      return this._onkoTanaan(erapaiva) ? 'tänään' : 'huomenna'
    }
    const vuosi = erapaiva.year === this.tanaan().year ? '' : String(erapaiva.year % 100).padStart(2, '0')
    return `ep ${erapaiva.day}.${erapaiva.month}.${vuosi}`
  }

  private _onkoTanaan(paiva: LocalDate) {
    return this._dateService.compareLocalDates(paiva, '==', this.tanaan())
  }
}
